import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { useAuth } from '@clerk/clerk-react';
import { Formik, Form, Field, ErrorMessage, FormikProps } from 'formik';
import { z } from 'zod';
import { useState } from 'react';
import { currencyList } from '~/config/currenciesStatic';
import { useFeatureFlagsFromClerk } from '~/hooks/useOrganizationConfig';
import { requiresPortfolioForDashboard } from '~/config/organizationConfig';
import { usePortfolios } from '~/hooks/usePortfolios';
import { Role } from '~/types/rbac';
import { authAtom, jotaiStore } from '~/atoms/authAtom';

interface AddEditDashboardDialogProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: {
    name: string;
    icon?: string | null;
    id?: string;
    isDefault: boolean;
    selectedPortfolioId?: string;
    useDefaultLayout?: boolean;
  }) => Promise<void>;
  mode?: 'create' | 'edit';
  initialData?: {
    name: string;
    icon?: string;
    id: string;
    isDefault: boolean;
    portfolioId?: string;
  };
}

interface Portfolio {
  id: string;
  name: string;
  fund_code: string;
  base_currency: string;
  fund_type: string | { name?: string; label?: string; value?: string };
}

interface PortfolioOption {
  label: string;
  value: Portfolio;
  fundType: string;
  fundCode: string;
  currency: string;
}

interface FormValues {
  name: string;
  icon: string | null;
  isDefault: boolean;
  useDefaultLayout: boolean;
  selectedPortfolio: Portfolio | null;
}

// Validation schema
const getValidationSchema = (
  isPortfolioRequired: boolean,
  isTestUser: boolean,
) => {
  const baseSchema = {
    name: z
      .string()
      .min(2, 'Dashboard name must be at least 2 characters')
      .max(50, 'Dashboard name must be less than 50 characters'),
    icon: z.union([z.string(), z.null(), z.undefined()]).optional(),
    isDefault: z.boolean(),
    useDefaultLayout: z.boolean(),
  };

  // Portfolio is required only if org requires it AND user is not a test user
  if (isPortfolioRequired && !isTestUser) {
    return z.object({
      ...baseSchema,
      selectedPortfolio: z
        .object({
          id: z.string().min(1, 'Portfolio ID is required'),
          name: z.string().min(1, 'Portfolio name is required'),
          fundCode: z.string().optional(),
          currency: z.string().optional(),
          fundType: z.string().optional(),
        })
        .nullable()
        .refine((value) => value !== null && Boolean(value && value.id), {
          message: 'Please select a portfolio',
        }),
    });
  }

  // For non-required orgs or test users, portfolio is completely optional
  return z.object({
    ...baseSchema,
    selectedPortfolio: z.any().optional().nullable(),
  });
};

// Custom validation function for Formik
const validateForm = (
  values: FormValues,
  isPortfolioRequired: boolean,
  isTestUser: boolean,
) => {
  try {
    const schema = getValidationSchema(isPortfolioRequired, isTestUser);
    schema.parse(values);
    return {};
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors: Record<string, string> = {};
      error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      return fieldErrors;
    }
    return {};
  }
};

export const AddEditDashboardDialog = ({
  visible,
  onHide,
  onSubmit,
  mode = 'create',
  initialData,
}: AddEditDashboardDialogProps) => {
  const auth = useAuth();
  const featureFlags = useFeatureFlagsFromClerk();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if portfolio is required (from Clerk org publicMetadata feature flags)
  const isPortfolioRequired = requiresPortfolioForDashboard(featureFlags);
  const authData = jotaiStore.get(authAtom);
  const userRole = authData?.auth?.orgRole?.split(':')[1] as Role | undefined;
  const isTestUser = userRole === 'test_user';
  const iconOptions = [
    { label: 'Chart', value: 'pi pi-chart-bar', icon: 'pi pi-chart-bar' },
    { label: 'Table', value: 'pi pi-table', icon: 'pi pi-table' },
    { label: 'Calendar', value: 'pi pi-calendar', icon: 'pi pi-calendar' },
    { label: 'List', value: 'pi pi-list', icon: 'pi pi-list' },
    { label: 'Map', value: 'pi pi-map', icon: 'pi pi-map' },
  ];

  // Fetch portfolios only for organizations that require them and when dialog is visible
  const { portfolios, loading: portfoliosLoading } = usePortfolios(
    !isPortfolioRequired || !visible,
  );

  // Transform portfolios for dropdown
  const portfolioOptions: PortfolioOption[] = portfolios.map(
    (portfolio: Portfolio) => {
      // Helper to safely extract string from fund_type (could be object)
      const getFundTypeString = (
        fundType: string | { name?: string; label?: string; value?: string },
      ): string => {
        if (!fundType) return '';
        if (typeof fundType === 'string') return fundType;
        if (typeof fundType === 'object') {
          if (fundType.name) return String(fundType.name);
          if (fundType.label) return String(fundType.label);
          if (fundType.value) return String(fundType.value);
          return '';
        }
        return String(fundType);
      };

      return {
        label: String(portfolio.name || 'Unnamed Portfolio'),
        value: portfolio,
        fundType: getFundTypeString(portfolio.fund_type),
        fundCode: String(portfolio.fund_code || ''),
        currency: String(portfolio.base_currency || ''),
      };
    },
  );

  // Initial form values
  const initialValues: FormValues = {
    name: initialData?.name || '',
    icon: initialData?.icon || null,
    isDefault: initialData?.isDefault || false,
    useDefaultLayout: false, // Only available for new dashboards
    selectedPortfolio: initialData?.portfolioId
      ? portfolioOptions.find((p) => p.value.id === initialData.portfolioId)
          ?.value || null
      : null,
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      console.log('values', values);
      setIsSubmitting(true);

      // Include portfolio ID if selected (for both SL and non-SL users)
      const submitData = {
        name: values.name,
        icon: values.icon,
        id: initialData?.id,
        isDefault: values.isDefault,
        useDefaultLayout: values.useDefaultLayout,
        ...(values.selectedPortfolio?.id && {
          selectedPortfolioId: values.selectedPortfolio.id,
        }),
      };

      await onSubmit(submitData);
      onHide(); // Close dialog on success
    } catch (error) {
      console.error('Error submitting dashboard:', error);
      // Keep dialog open on error
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHide = () => {
    onHide();
  };

  const optionTemplate = (option: { label: string; icon: string }) => {
    return (
      <div className="flex align-items-center gap-2">
        <i className={option.icon}></i>
        <span>{option.label}</span>
      </div>
    );
  };

  const selectedOptionTemplate = (
    option: { label: string; icon: string } | null,
  ) => {
    if (!option || !option.icon) return <span>Select an icon (optional)</span>;
    return (
      <div className="flex align-items-center gap-2">
        <i className={option.icon}></i>
        <span>{option.label}</span>
      </div>
    );
  };

  // Helper function to safely render values
  const safeString = (value: string | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const portfolioOptionTemplate = (option: PortfolioOption) => {
    const fundCode = safeString(option.fundCode);
    const currency = safeString(option.currency);

    // Find currency label for better display
    const currencyInfo = currencyList.find((c) => c.value === currency);
    const currencyDisplay = currencyInfo
      ? `${currencyInfo.label} (${currency})`
      : currency;

    const details = [fundCode, currencyDisplay].filter(Boolean).join(' • ');

    return (
      <div className="flex flex-column gap-1">
        <div className="font-semibold text-900">{safeString(option.label)}</div>
        {details && <div className="text-sm text-600">{details}</div>}
      </div>
    );
  };

  const selectedPortfolioTemplate = (option: PortfolioOption | null) => {
    if (!option) return <span>Select a portfolio</span>;

    const fundCode = safeString(option.fundCode);
    const currency = safeString(option.currency);

    // Find currency label for better display
    const currencyInfo = currencyList.find((c) => c.value === currency);
    const currencyDisplay = currencyInfo
      ? `${currencyInfo.label} (${currency})`
      : currency;

    const details = [fundCode, currencyDisplay].filter(Boolean).join(' • ');

    return (
      <div className="flex flex-column gap-1">
        <div className="font-semibold text-900">{safeString(option.label)}</div>
        {details && <div className="text-sm text-600">{details}</div>}
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex justify-content-end align-items-center gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleHide}
        severity="secondary"
        disabled={isSubmitting}
      />
      <Button
        label={mode === 'create' ? 'Create' : 'Update'}
        icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        type="submit"
        form="dashboard-form"
        autoFocus
        loading={isSubmitting}
        disabled={isSubmitting}
      />
    </div>
  );

  return (
    <>
      <Dialog
        header={mode === 'create' ? 'Create New Dashboard' : 'Edit Dashboard'}
        visible={visible}
        style={{ width: '50vw' }}
        className="p-fluid"
        footer={dialogFooter}
        onHide={handleHide}
        pt={{
          header: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              color: 'var(--bq-text-primary)',
              padding: '1rem 1.5rem',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
            },
          },
          content: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              color: 'var(--bq-text-primary)',
            },
          },
          footer: {
            style: {
              backgroundColor: 'var(--bq-surface-elevated)',
              borderTop: '1px solid var(--bq-border-muted)',
              padding: '1rem 1.5rem',
              minHeight: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            },
          },
        }}
      >
        <Formik
          initialValues={initialValues}
          validate={(values) =>
            validateForm(values, isPortfolioRequired, isTestUser)
          }
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            handleSubmit: formikHandleSubmit,
          }: FormikProps<FormValues>) => {
            // Debug: Log validation errors
            if (Object.keys(errors).length > 0) {
              console.log('Form validation errors:', errors);
            }

            return (
              <Form
                id="dashboard-form"
                className="dark-form"
                onSubmit={formikHandleSubmit}
              >
                <div
                  style={{
                    height: '1px',
                    backgroundColor: 'var(--bq-border-muted)',
                    margin: '0 -1.5rem',
                    width: 'calc(100% + 3rem)',
                  }}
                />

                <div className="grid pt-3">
                  <div className="col-12">
                    <div className="field">
                      <label htmlFor="name" className="font-bold block mb-2">
                        Dashboard Name
                      </label>
                      <Field
                        as={InputText}
                        id="name"
                        name="name"
                        className={`w-full ${errors.name && touched.name ? 'p-invalid' : ''}`}
                        placeholder="Enter dashboard name"
                      />
                      <ErrorMessage
                        name="name"
                        component="small"
                        className="text-red-400"
                      />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="field">
                      <label htmlFor="icon" className="font-bold block mb-2">
                        Dashboard Icon
                      </label>
                      <Field
                        as={Dropdown}
                        id="icon"
                        name="icon"
                        options={iconOptions}
                        showClear
                        placeholder="Select an icon (optional)"
                        className="w-full"
                        optionLabel="label"
                        itemTemplate={optionTemplate}
                        valueTemplate={selectedOptionTemplate}
                        onChange={(e: { value: string | null }) => {
                          setFieldValue('icon', e.value || null);
                          setFieldTouched('icon', true);
                        }}
                      />
                    </div>
                  </div>

                  {requiresPortfolioForDashboard(featureFlags) &&
                    !isTestUser && (
                      <div className="col-12">
                        <div className="field">
                          <label className="font-bold block mb-2">
                            Portfolio{' '}
                            {isPortfolioRequired && (
                              <span className="text-red-400">*</span>
                            )}
                          </label>
                          <Field
                            as={Dropdown}
                            name="selectedPortfolio"
                            options={portfolioOptions}
                            placeholder="Select a portfolio"
                            className={`w-full ${errors.selectedPortfolio && touched.selectedPortfolio ? 'p-invalid' : ''}`}
                            optionLabel="label"
                            itemTemplate={portfolioOptionTemplate}
                            valueTemplate={selectedPortfolioTemplate}
                            loading={portfoliosLoading}
                            loadingIcon="pi pi-spin pi-spinner"
                            showClear={!isPortfolioRequired}
                            onChange={(e: { value: Portfolio | null }) =>
                              setFieldValue('selectedPortfolio', e.value)
                            }
                          />
                          <ErrorMessage
                            name="selectedPortfolio"
                            component="small"
                            className="text-red-400"
                          />
                        </div>
                      </div>
                    )}

                  <div className="col-12">
                    <div className="flex flex-column gap-2">
                      <div className="flex align-items-center">
                        <Checkbox
                          inputId="isDefault"
                          checked={values.isDefault}
                          onChange={(e) => {
                            setFieldValue('isDefault', e.checked ?? false);
                            setFieldTouched('isDefault', true);
                          }}
                          onBlur={() => setFieldTouched('isDefault', true)}
                        />
                        <label htmlFor="isDefault" className="ml-2">
                          Set as default dashboard
                        </label>
                      </div>

                      {mode === 'create' && (
                        <div className="flex align-items-center gap-2">
                          <Checkbox
                            inputId="useDefaultLayout"
                            checked={values.useDefaultLayout}
                            onChange={(e) => {
                              setFieldValue(
                                'useDefaultLayout',
                                e.checked ?? false,
                              );
                              setFieldTouched('useDefaultLayout', true);
                            }}
                            onBlur={() =>
                              setFieldTouched('useDefaultLayout', true)
                            }
                          />
                          <label htmlFor="useDefaultLayout">
                            Use default layout with standard widgets
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};
